import { useState, useMemo } from 'react'
import { Navigate } from 'react-router-dom'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion, PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { BeforePlanSubscriptionC } from '@/components/beforePlanSubscription/typeC'
import { CompanyContacts } from '@/components/companyContacts'
import { PageTitle } from '@/components/pageTitle'
import { PaymentMethodSwitcher } from '@/components/paymentMethodSwitcher'
import { PaymentScreenDescription } from '@/components/paymentScreenDescription'
import { Receipt } from '@/components/receipt'
import { InvertedShield } from '@/shared/icons/InvertedShield'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'

import { paypalButtonMap, stripeCheckoutFormMap, walletPaymentButtonMap } from './componentMappings'
import { useProcessContactState } from './hooks/contactState/useProcessContactState'
import useTrackPageAndPaymentScreen from './hooks/trackWebEvents/useTrackWebEvents'
import { TPaymentScreenPageProps } from './types'

const paypalOptions = getDefaultPayPalOptions()

const pageName = PageName.paymentScreen

export const PaymentScreenPage = ({
  isAdditional = false,
  isShowDescription,
  getItem,
  setItem,
  pageConfig,
  selectPlanPage,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
  contactStateRoutesMap,
}: TPaymentScreenPageProps) => {
  const { user } = useAuth()
  const { checkoutFlowVersion } = useSession()
  const {
    discount,
    title,
    pageTitle,
    paymentMethodTitle,
    receiptData,
    receiptTitle,
    stripeFormConfig,
    walletButtonConfig,
    paypalCustomTagline,
    showGuaranteesLabel,
    showCompanyContacts,
  } = pageConfig

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const selectedPlanId = getItem(PageName.selectPlan)
  const planData = useMemo(
    () => selectPlanPage.plansDetails.find((plan) => plan.planId === selectedPlanId),
    [selectPlanPage.plansDetails, selectedPlanId]
  )

  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(
    showPayPal ? PaymentMethod.PAYPAL : PaymentMethod.CARD
  )

  const selectPlanPageBlocks = selectPlanPage.blocks
  const lastBlock = selectPlanPageBlocks[selectPlanPageBlocks.length - 1]
  const blockForDescription =
    lastBlock && typeof lastBlock === 'function' && planData && lastBlock(planData)

  const selectedPlan = getItem(PageName.selectPlan)

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(planData, stripeFormAppearance),
    [planData, stripeFormAppearance]
  )

  const className = `page page-${pageName}`

  useTrackPageAndPaymentScreen(config, getItem, setItem, pageTitle, planData, pageName)

  const contactStateEl = useProcessContactState(
    config.PAGE_SET_ID,
    getItem,
    setItem,
    { className, stateRoutes: contactStateRoutesMap },
    checkoutFlowVersion
  )
  if (contactStateEl) {
    return contactStateEl
  }

  if (!user && checkoutFlowVersion !== CheckoutFlowVersion.V2) {
    // TODO: probably there are better typescript hints to avoid this check here
    return <Navigate to={getPath(PageName.registration)} />
  }

  if (!planData || !selectedPlanId) {
    return <Navigate to={getPath(PageName.selectPlan)} />
  }

  const changePaymentMethod = (type: PaymentMethod) => {
    setCurrentPaymentMethod(type)
  }

  if (!stripeOptions) {
    return null
  }

  const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
  const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
  const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <div className="offer">{title}</div>
          <PageTitle title={discount} />
          <div className="payment-method">{paymentMethodTitle}</div>
          <div className={'floating-container ' + (isAdditional ? 'extended' : '')}>
            <PaymentMethodSwitcher
              onChange={changePaymentMethod}
              currentPaymentMethod={currentPaymentMethod}
              showPayPal={showPayPal}
              showStripe={showStripe}
            />

            {showStripe && (!walletButtonConfig || !walletButtonConfig.underForm) && (
              <WalletPaymentFC
                planData={planData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            {receiptData && selectedPlan && (
              <Receipt receiptData={receiptData[selectedPlan]} receiptTitle={receiptTitle} />
            )}

            {showPayPal && currentPaymentMethod === PaymentMethod.PAYPAL && (
              <div className="paypal-container">
                <PayPalButtonFC
                  pageName={pageName}
                  paypalButtonConfigStyle={paypalButtonConfigStyle}
                  productData={planData}
                  getItem={getItem}
                  setItem={setItem}
                  config={config}
                  customTagline={paypalCustomTagline}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              </div>
            )}
            {showStripe && currentPaymentMethod === PaymentMethod.CARD && (
              <StripeCheckoutFormFC
                pageName={pageName}
                getItem={getItem}
                setItem={setItem}
                planDetails={planData}
                config={config}
                formConfig={stripeFormConfig}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}
            {showStripe && walletButtonConfig && walletButtonConfig.underForm && (
              <WalletPaymentFC
                planData={planData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                showDelimiter={walletButtonConfig.showDelimiter}
                buttonsDelimiterText={walletButtonConfig.delimiterText}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            {!isAdditional && !isNaN(planData.trialPrice) && planData.trialPrice && (
              <BeforePlanSubscriptionC key="before-plan" selectedPlan={planData} />
            )}
            {/* TBD: showCancelContainer is false */}
            {/*{!isAdditional && showCancelContainer && (*/}
            {/*  <div className="cancel-container">*/}
            {/*    <RoundCheckbox />*/}
            {/*    <span className="no-commitments">No commitments, cancel anytime ✨</span>*/}
            {/*  </div>*/}
            {/*)}*/}
            {isAdditional && !isShowDescription && (
              <PaymentScreenDescription blockNode={blockForDescription} />
            )}
            {showGuaranteesLabel && (
              <div className="guarantees-container">
                <InvertedShield />
                Guaranteed security payments
              </div>
            )}
            {showCompanyContacts && <CompanyContacts />}
          </div>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
